import { useEffect, useState } from 'react';
import FormInput from '../../../components/forms/input';
import FormSelect from '../../../components/forms/select';
import { LuCheck } from 'react-icons/lu';
import type { CaseFormData, PartyEntry } from '../../types';
import FileUploadZone from './FileUploadZone';
import SimplePartyFields from './SimplePartyFields';

const MANDATORY_DOCS = [
    'Writ of Summons',
    'Motion Exparte (M)',
    'Notice of Petition',
    'Written Address',
    'Statement of Claim',
];

interface CourtReferralFormProps {
    data: CaseFormData;
    onUpdate: (u: Partial<CaseFormData>) => void;
    onSubmit: () => void;
}

export default function CourtReferralForm({ data, onUpdate, onSubmit }: CourtReferralFormProps) {
    const [errors, setErrors] = useState<Record<string, any>>({});
    const emptyParty: PartyEntry = { name: '', address: '', phone: '', email: '' };

    /** Auto-tick mandatory docs in order as files are added; untick when files are removed. */
    useEffect(() => {
        const checkedCount = Math.min(data.files.length, MANDATORY_DOCS.length);
        const nextChecked = new Set(MANDATORY_DOCS.slice(0, checkedCount));
        const current = data.uploadedDocs ?? new Set<string>();
        const sameSize = current.size === nextChecked.size;
        const sameMembers = sameSize && MANDATORY_DOCS.slice(0, checkedCount).every((d) => current.has(d));
        if (!sameMembers) onUpdate({ uploadedDocs: nextChecked });
    }, [data.files.length, data.uploadedDocs, onUpdate]);

    const validate = () => {
        const newErrors: Record<string, any> = {};

        if (!data.filingDate) newErrors.filingDate = 'Filing date is required';
        if (!data.caseType) newErrors.caseType = 'Case type is required';
        if (!data.suitNumber) newErrors.suitNumber = 'Suit number is required';

        if (!data.lawyerName) newErrors.lawyerName = 'Lawyer name is required';
        if (!data.lawyerPhone) newErrors.lawyerPhone = 'Phone is required';
        if (data.lawyerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.lawyerEmail)) {
            newErrors.lawyerEmail = 'Invalid email';
        }

        const defendantErrors: any = {};
        if (!data.defendant?.name) defendantErrors.name = 'Name is required';
        if (Object.keys(defendantErrors).length > 0) newErrors.defendant = defendantErrors;

        const claimantErrors: any = {};
        if (!data.claimant?.name) claimantErrors.name = 'Name is required';
        if (Object.keys(claimantErrors).length > 0) newErrors.claimant = claimantErrors;

        if (data.files.length < MANDATORY_DOCS.length) {
            newErrors.mandatoryDocs = `All ${MANDATORY_DOCS.length} mandatory documents must be uploaded`;
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validate()) onSubmit();
    };

    const clearError = (field: string) => {
        if (errors[field]) {
            const next = { ...errors };
            delete next[field];
            setErrors(next);
        }
    };

    const clearPartyError = (party: 'defendant' | 'claimant', field: string) => {
        if (errors[party]?.[field]) {
            const next = { ...errors };
            const partyErrors = { ...next[party] };
            delete partyErrors[field];
            if (Object.keys(partyErrors).length === 0) delete next[party];
            else next[party] = partyErrors;
            setErrors(next);
        }
    };

    return (
        <div className="space-y-10">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <FormInput
                    title="Date of Filing"
                    id="date-of-filing"
                    type="datetime-local"
                    value={data.filingDate || ''}
                    onChange={(e) => {
                        onUpdate({ filingDate: e.target.value });
                        clearError('filingDate');
                    }}
                    variant="white"
                    error={errors.filingDate}
                />
                <div>
                    <FormSelect
                        title="Type of Case"
                        id="type-of-case"
                        placeholder="Select type of case"
                        value={data.caseType || ''}
                        options={[
                            { label: 'Civil', value: 'Civil' },
                            { label: 'Criminal', value: 'Criminal' },
                        ]}
                        onChange={(value) => {
                            onUpdate({ caseType: value });
                            clearError('caseType');
                        }}
                    />
                    {errors.caseType && <p className="mt-1 text-xs text-red-500">{errors.caseType}</p>}
                </div>
                <FormInput
                    title="Suit Number"
                    id="suit-number"
                    placeholder="Suit Number"
                    value={data.suitNumber || ''}
                    onChange={(e) => {
                        onUpdate({ suitNumber: e.target.value });
                        clearError('suitNumber');
                    }}
                    variant="white"
                    error={errors.suitNumber}
                />
            </div>

            <div className="space-y-4">
                <h2 className="text-xl font-bold text-secondary">User/Lawyer Details</h2>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <FormInput
                        title="User/Lawyer Name"
                        id="lawyer-name"
                        placeholder="Enter Lawyer's Name"
                        value={data.lawyerName || ''}
                        onChange={(e) => {
                            onUpdate({ lawyerName: e.target.value });
                            clearError('lawyerName');
                        }}
                        variant="white"
                        error={errors.lawyerName}
                    />
                    <FormInput
                        title="Law/Firm Organisation"
                        id="law-firm"
                        placeholder="Enter Firm"
                        value={data.lawFirm || ''}
                        onChange={(e) => onUpdate({ lawFirm: e.target.value })}
                        variant="white"
                    />
                    <FormInput
                        title="Phone Number"
                        id="lawyer-phone"
                        placeholder="e.g. +234 801 234 5678"
                        value={data.lawyerPhone || ''}
                        onChange={(e) => {
                            onUpdate({ lawyerPhone: e.target.value });
                            clearError('lawyerPhone');
                        }}
                        variant="white"
                        error={errors.lawyerPhone}
                    />
                    <FormInput
                        title="Email"
                        id="lawyer-email"
                        type="email"
                        placeholder="Enter email"
                        value={data.lawyerEmail || ''}
                        onChange={(e) => {
                            onUpdate({ lawyerEmail: e.target.value });
                            clearError('lawyerEmail');
                        }}
                        variant="white"
                        error={errors.lawyerEmail}
                    />
                </div>
            </div>

            <div className="space-y-4">
                <h2 className="text-xl font-bold text-secondary">Defendant's Details</h2>
                <SimplePartyFields
                    entry={data.defendant || emptyParty}
                    errors={errors.defendant}
                    onUpdate={(field, value) => {
                        onUpdate({ defendant: { ...(data.defendant || emptyParty), [field]: value } });
                        clearPartyError('defendant', field);
                    }}
                    nameLabel="Name"
                    id="defendant"
                />
            </div>

            <div className="space-y-4">
                <h2 className="text-xl font-bold text-secondary">Claimant's Details</h2>
                <SimplePartyFields
                    entry={data.claimant || emptyParty}
                    errors={errors.claimant}
                    onUpdate={(field, value) => {
                        onUpdate({ claimant: { ...(data.claimant || emptyParty), [field]: value } });
                        clearPartyError('claimant', field);
                    }}
                    nameLabel="Name"
                    id="claimant"
                />
            </div>

            <div className="space-y-4">
                <h2 className="text-xl font-bold text-secondary">Statement of Issues & Evidence</h2>
                <div>
                    <p className="mb-3 text-[14px] font-semibold text-secondary">
                        Mandatory Documents <span className="text-red-500">*</span>
                    </p>
                    <div className="flex flex-wrap gap-4">
                        {MANDATORY_DOCS.map((doc) => (
                            <div key={doc} className="flex items-center gap-2 text-[13px] text-secondary">
                                {doc}
                                <span
                                    className={`flex h-5 w-5 items-center justify-center rounded-full ${
                                        data.uploadedDocs?.has(doc)
                                            ? 'bg-green-500 text-white'
                                            : 'border border-primary text-primary'
                                    }`}
                                >
                                    <LuCheck size={12} />
                                </span>
                            </div>
                        ))}
                    </div>
                    {errors.mandatoryDocs && (
                        <p className="mt-2 text-xs text-red-500">{errors.mandatoryDocs}</p>
                    )}
                </div>
                <FileUploadZone files={data.files} setFiles={(fn) => onUpdate({ files: fn(data.files) })} />
            </div>

            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={handleSubmit}
                    className="rounded-xl bg-secondary px-14 py-3 text-[14px] font-semibold text-white transition-colors hover:bg-secondary-400"
                >
                    Submit
                </button>
            </div>
        </div>
    );
}
